import { FC, useEffect, useState } from "react";
import { UserActionPlan } from "@/data/interfaces/UserActionPlan";
import userActionPlanService from "@/services/userActionPlanService";
import { ActionPlanCard } from "./ActionPlanCard";
import { ErrorMessage } from "@/components/UI";
import { Dumbbell, Zap, Salad } from "lucide-react";

const FALLBACK_ICONS = [
  <Dumbbell className="h-6 w-6 text-orange-500" />,
  <Zap className="h-6 w-6 text-orange-500" />,
  <Salad className="h-6 w-6 text-green-500" />,
];

export const ActionPlanCards: FC = () => {
  const [actionPlans, setActionPlans] = useState<UserActionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    loadActionPlans();
  }, []);

  const loadActionPlans = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(undefined);
      const data = await userActionPlanService.getMyActionPlans();
      setActionPlans(data);
    } catch (error) {
      console.error(error);
      setErrorMessage(`Failed to load action plans: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="text-center text-gray-500">Loading action plans...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ErrorMessage error={errorMessage} />

      {actionPlans.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {actionPlans.map((plan, index) => (
            <ActionPlanCard
              key={plan.id}
              title={plan.insight_type?.name ?? "Action plan"}
              subtitle="New Goal"
              description={plan.rules}
              imageUrl={plan.insight_type?.image ?? undefined}
              icon={FALLBACK_ICONS[index % FALLBACK_ICONS.length]}
              percentChange={plan.percent > 0 ? plan.percent : undefined}
              doctorName={
                plan.doctor
                  ? `${plan.doctor.name} ${plan.doctor.surname}`
                  : undefined
              }
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl bg-white p-6 text-center text-gray-500 shadow-sm">
          No action plans available.
        </div>
      )}
    </div>
  );
};
